/**
 * The folded-away explainer prose a panel keeps under its table.
 *
 * Born inside `FixtureListPanel` (the TD's 20 Sep morning ask — the tips were costing him a
 * fixture row) and moved here the same day when the Tech Table asked for the identical fold:
 * one component with a per-panel `storageKey`, rather than two copies that could drift on how
 * they remember.
 *
 * **A `<details>`, not a conditional render.** The sentences stay in the DOM whether the fold
 * is open or shut, so a browser's own find-in-page, a screen reader's document read and the
 * `data-testid`s the release tests read them by all still reach them. The element's own
 * `open` attribute is the state; `onToggle` is the only place the mark is written, so a fold
 * opened with the keyboard is remembered exactly as one opened with a click.
 *
 * **A third caller, and a summary of its own (27 Sep 2026).** The Desk's Cap Milestones panel
 * folds the milestones past its first three behind the same element, because the reasoning is
 * the same — shut by default, the TD's choice remembered per panel, everything still in the DOM.
 * What it folds is not tips, so `summary` and `cue` replace the row's words; left out, the row
 * reads exactly as it always has for the two tips folds.
 */

import { useEffect, useState } from "react";

function tipsWereOpened(storageKey: string): boolean {
  try {
    return window.localStorage.getItem(storageKey) === "1";
  } catch {
    // Private-mode Safari throws on `localStorage` outright. A fold that cannot remember is a
    // fold that opens closed, which is the harmless half of the failure and is also the default.
    return false;
  }
}

function rememberTips(storageKey: string, open: boolean): void {
  try {
    window.localStorage.setItem(storageKey, open ? "1" : "0");
  } catch {
    // See `tipsWereOpened` — nothing on these screens depends on the mark.
  }
}

export interface TipsFoldProps {
  /** `localStorage` key for this panel's fold. Per panel, as `HowToUse`'s is. */
  storageKey: string;
  testId: string;
  children: React.ReactNode;
  /** The summary row's words in place of "ⓘ How this page works" — e.g. the counts folded away. */
  summary?: React.ReactNode;
  /** The cue's words, shut and open, in place of "show tips" / "hide tips". */
  cue?: { shut: string; open: string };
  /** An extra class on the `<details>`, for a caller whose row sits in a tighter measure. */
  className?: string;
}

export function TipsFold({ storageKey, testId, children, summary, cue, className }: TipsFoldProps): React.JSX.Element {
  const [open, setOpen] = useState(false);

  // Read once on mount rather than on every render, as `HowToUse` does: `localStorage` is
  // synchronous and this sits under a table that redraws on every edit.
  useEffect(() => {
    setOpen(tipsWereOpened(storageKey));
  }, [storageKey]);

  return (
    <details
      className={className === undefined ? "tipsfold" : `tipsfold ${className}`}
      data-testid={testId}
      open={open}
      onToggle={(event) => {
        const next = event.currentTarget.open;
        setOpen(next);
        rememberTips(storageKey, next);
      }}
    >
      <summary className="tipsfold__row" data-testid={`${testId}-toggle`}>
        {summary ?? (
          <>
            <span aria-hidden="true">ⓘ</span> How this page works
          </>
        )}
        <span className="tipsfold__cue">{open ? (cue?.open ?? "hide tips") : (cue?.shut ?? "show tips")}</span>
      </summary>
      <div className="tipsfold__body">{children}</div>
    </details>
  );
}
